// src/utils/tournamentTokens.js
// Substitutes {{token}} placeholders in tournament info_html with live values
// from the tournament doc. Used by TournamentDetail (and any other page that
// renders tournament.info_html) to keep the admin-authored copy in sync with
// the actual dates/times stored on the doc.
//
// Token list is intentionally curated (not "every field") so admins get
// predictable output and we don't accidentally expose internal fields.

// Formats a YYYY-MM-DD date string as "Sat, Aug 12, 2026". Returns the raw
// value unchanged if it can't be parsed.
function formatDate(value) {
  if (!value) return '';
  // Parse YYYY-MM-DD locally — using new Date(string) interprets it as UTC
  // and can shift by a day in some time zones.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return String(value);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

// Formats a HH:MM 24h time string as "8:00 AM" / "3:30 PM".
function formatTime(value) {
  if (!value) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(String(value));
  if (!m) return String(value);
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

// Maps token name → (tournament) => formatted string. Keep in sync with the
// dropdown options in TournamentForm.js Tab 4 (Info Page).
const FORMATTERS = {
  name: t => t?.name || '',
  date: t => formatDate(t?.date),
  eventStartTime: t => formatTime(t?.eventStartTime),
  eventArrivalTime: t => formatTime(t?.eventArrivalTime),
  eventEndTime: t => formatTime(t?.eventEndTime),
  close_date: t => formatDate(t?.close_date),
  close_time: t => formatTime(t?.close_time),
  refund_date: t => formatDate(t?.refund_date),
  refund_time: t => formatTime(t?.refund_time),
  late_date: t => formatDate(t?.late_date),
  late_time: t => formatTime(t?.late_time),
  sanctioning_body: t => t?.sanctioning_body || '',
};

// Replace every {{token}} in html with its formatted value from the
// tournament doc. Unknown tokens are left as-is (so admins can see the
// missing reference and fix it).
export function substituteTokens(html, tournament) {
  if (!html || !tournament) return html || '';
  return String(html).replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (match, key) => {
    const fn = FORMATTERS[key];
    if (!fn) return match;
    return fn(tournament) || '';
  });
}
